import { addSupplierToCompany, createSupplier as createSupplierAPI, getSuppliersByCompany } from '@/modules/suppliers/redux/SupplierCRUD';
import { createMaterialGroupOption, getMaterialGroupOptions } from '../redux/ItemGroupCRUD';
import { createManufacturerOption, getManufacturerOptions } from '../redux/ItemManufacturerCRUD';
import { createMaterialTypeOption, getMaterialTypeOptions } from '../redux/ItemTypeCRUD';
import { createItemUomBackend, getItemUomBackendOptions, toCompanyScopedUomOptions } from '../redux/ItemUomCRUD';

export interface InventoryItemOption {
  id: string;
  name: string;
  company_id?: string;
}

export interface InventoryItemUomConversion {
  id: string;
  from_uom_id: string;
  to_uom_id: string;
  ratio: number;
  company_id: string;
}

export interface InventoryItemSupplierDraft {
  name: string;
  email?: string;
  phone?: string;
  address?: {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
}

export interface InventoryItemRow {
  id: string;
  code: string;
  name: string;
  item_type: string;
  item_type_label: string;
  supplier_id: string;
  supplier_name: string;
  manufacturer_id: string;
  manufacturer_name: string;
  group_id: string;
  group_name: string;
  uom_id: string;
  uom_name: string;
  status: string;
  status_label: string;
  company_id: string;
  company_name: string;
  specification: string;
  image1: string;
  image2: string;
  track_lot: boolean;
  track_location: boolean;
  last_price?: string;
}

export interface InventoryItemFormData {
  code: string;
  name: string;
  group_id: string;
  supplier_id: string;
  item_type: string;
  uom_id: string;
  manufacturer_id: string;
  status: string;
  company_id: string;
  specification: string;
  image1: string;
  image2: string;
  track_lot: boolean;
  track_location: boolean;
}

export interface InventoryItemQueryParams {
  search?: string;
  status?: string;
  companyId?: string;
  page?: number;
  pageSize?: number;
}

export interface InventoryItemListResponse {
  results: InventoryItemRow[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export type InventoryItemPayload = InventoryItemFormData;

export const itemStatusOptions = [
  { id: 'all', name: 'Tất cả' },
  { id: 'yeu_cau_vat_tu', name: 'Yêu cầu vật tư', code: 'YEU_CAU_VAT_TU' },
  { id: 'draft', name: 'Nháp', code: 'DRAFT' },
  { id: 'approved', name: 'Đang dùng', code: 'APPROVED' },
  { id: 'inactive', name: 'Ngưng dùng', code: 'INACTIVE' },
];

export const itemTypeOptions = [
  { id: 'material', name: 'Vật tư' },
  { id: 'commercial', name: 'Hàng thương mại' },
];

const mutableItemTypeOptions: InventoryItemOption[] = [...itemTypeOptions];
const mutableItemGroupOptions: InventoryItemOption[] = [];
const loadedGroupCompanies = new Set<string>();
const loadedTypeCompanies = new Set<string>();
const loadedManufacturerCompanies = new Set<string>();
const loadedSupplierCompanies = new Set<string>();
const loadedUomCompanies = new Set<string>();
const mutableManufacturerOptions: InventoryItemOption[] = [];
const mutableSupplierOptions: InventoryItemOption[] = [];
const mutableSupplierDrafts: Array<InventoryItemSupplierDraft & { id: string; company_id: string }> = [];
const mutableUomOptions: InventoryItemOption[] = [];
const mutableUomConversions: InventoryItemUomConversion[] = [];

const resolveStatusLabel = (status: string) => itemStatusOptions.find((option) => option.id === status)?.name || status;
const resolveItemTypeLabel = (itemType: string) => mutableItemTypeOptions.find((option) => option.id === itemType)?.name || itemType;

const getCompanyName = (_companyId: string) => 'Chưa rõ công ty';
const normalizeOptionText = (value?: string | null) => value?.trim().toLowerCase() || '';

const resolveCreatedSupplierId = async (
  companyId: string,
  createdSupplier: any,
  draft: InventoryItemSupplierDraft,
): Promise<string | undefined> => {
  if (createdSupplier?.id) {
    return String(createdSupplier.id);
  }

  const response = await getSuppliersByCompany(companyId, { page: 1, page_size: 500 });
  const suppliers = response?.results || [];

  const matchedSupplier = suppliers.find((supplier: any) => {
    if (createdSupplier?.code && supplier?.code === createdSupplier.code) {
      return true;
    }

    const expectedName = normalizeOptionText(createdSupplier?.name || draft.name);
    if (!expectedName || normalizeOptionText(supplier?.name) !== expectedName) {
      return false;
    }

    const expectedPhone = normalizeOptionText(createdSupplier?.phone || draft.phone);
    const expectedEmail = normalizeOptionText(createdSupplier?.email || draft.email);
    const phoneMatches = !expectedPhone || normalizeOptionText(supplier?.phone) === expectedPhone;
    const emailMatches = !expectedEmail || normalizeOptionText(supplier?.email) === expectedEmail;

    return phoneMatches && emailMatches;
  });

  return matchedSupplier?.id ? String(matchedSupplier.id) : undefined;
};

const filterOptionsByCompany = (options: InventoryItemOption[], companyId?: string) => {
  if (!companyId) {
    return options;
  }

  return options.filter((option) => option.company_id === companyId);
};

export const getItemGroupOptions = (_companyId?: string) => [...mutableItemGroupOptions];

const upsertGroupOption = (option: InventoryItemOption) => {
  const index = mutableItemGroupOptions.findIndex((group) => group.id === option.id);
  if (index >= 0) {
    mutableItemGroupOptions[index] = option;
    return;
  }
  mutableItemGroupOptions.push(option);
};

export const loadItemGroupOptions = async (_companyId?: string, force = false): Promise<InventoryItemOption[]> => {
  const key = '__all__';
  if (!force && loadedGroupCompanies.has(key)) {
    return getItemGroupOptions();
  }

  const apiOptions = await getMaterialGroupOptions();
  mutableItemGroupOptions.length = 0;
  mutableItemGroupOptions.push(...apiOptions);

  loadedGroupCompanies.add(key);
  return getItemGroupOptions();
};

export const getItemUomOptions = (companyId?: string): InventoryItemOption[] => {
  return filterOptionsByCompany(mutableUomOptions, companyId);
};

export const loadItemUomOptions = async (companyId?: string, force = false): Promise<InventoryItemOption[]> => {
  if (!companyId) {
    return getItemUomOptions();
  }

  if (!force && loadedUomCompanies.has(companyId)) {
    return getItemUomOptions(companyId);
  }

  const baseOptions = await getItemUomBackendOptions();
  const apiOptions = toCompanyScopedUomOptions(baseOptions, companyId);
  const preserved = mutableUomOptions.filter((uom) => uom.company_id !== companyId);
  const nextById = new Map<string, InventoryItemOption>();
  preserved.forEach((option) => nextById.set(option.id, option));
  apiOptions.forEach((option) => nextById.set(option.id, option));
  mutableUomOptions.length = 0;
  mutableUomOptions.push(...nextById.values());
  loadedUomCompanies.add(companyId);
  return getItemUomOptions(companyId);
};

export const getItemManufacturerOptions = (companyId?: string): InventoryItemOption[] => {
  return filterOptionsByCompany(mutableManufacturerOptions, companyId);
};

const upsertManufacturerOption = (option: InventoryItemOption) => {
  const index = mutableManufacturerOptions.findIndex((manufacturer) => manufacturer.id === option.id);
  if (index >= 0) {
    mutableManufacturerOptions[index] = option;
    return;
  }
  mutableManufacturerOptions.push(option);
};

export const loadItemManufacturerOptions = async (companyId?: string, force = false): Promise<InventoryItemOption[]> => {
  if (!companyId) {
    return getItemManufacturerOptions();
  }

  if (!force && loadedManufacturerCompanies.has(companyId)) {
    return getItemManufacturerOptions(companyId);
  }

  const apiOptions = await getManufacturerOptions(companyId);
  const preserved = mutableManufacturerOptions.filter((manufacturer) => manufacturer.company_id !== companyId);
  const nextById = new Map<string, InventoryItemOption>();
  preserved.forEach((option) => nextById.set(option.id, option));
  apiOptions.forEach((option) => nextById.set(option.id, option));
  mutableManufacturerOptions.length = 0;
  mutableManufacturerOptions.push(...nextById.values());
  loadedManufacturerCompanies.add(companyId);
  return getItemManufacturerOptions(companyId);
};

export const getItemSupplierOptions = (companyId?: string): InventoryItemOption[] => {
  return filterOptionsByCompany(mutableSupplierOptions, companyId);
};

const upsertSupplierOption = (option: InventoryItemOption) => {
  const index = mutableSupplierOptions.findIndex((supplier) => supplier.id === option.id);
  if (index >= 0) {
    mutableSupplierOptions[index] = option;
    return;
  }
  mutableSupplierOptions.push(option);
};

export const loadItemSupplierOptions = async (companyId?: string, force = false): Promise<InventoryItemOption[]> => {
  if (!companyId) {
    return getItemSupplierOptions();
  }

  if (!force && loadedSupplierCompanies.has(companyId)) {
    return getItemSupplierOptions(companyId);
  }

  const response = await getSuppliersByCompany(companyId, { page: 1, page_size: 500 });
  const apiOptions: InventoryItemOption[] = (response?.results || []).map((supplier: any) => ({
    id: String(supplier.id),
    name: supplier.name || supplier.code || String(supplier.id),
    company_id: companyId,
  }));

  const preserved = mutableSupplierOptions.filter((supplier) => supplier.company_id !== companyId);
  const nextById = new Map<string, InventoryItemOption>();
  preserved.forEach((option) => nextById.set(option.id, option));
  apiOptions.forEach((option) => nextById.set(option.id, option));
  mutableSupplierOptions.length = 0;
  mutableSupplierOptions.push(...nextById.values());
  loadedSupplierCompanies.add(companyId);
  return getItemSupplierOptions(companyId);
};

export const getItemTypeOptions = (_companyId?: string): InventoryItemOption[] => [...mutableItemTypeOptions];

const upsertTypeOption = (option: InventoryItemOption) => {
  const index = mutableItemTypeOptions.findIndex((itemType) => itemType.id === option.id);
  if (index >= 0) {
    mutableItemTypeOptions[index] = option;
    return;
  }
  mutableItemTypeOptions.push(option);
};

export const loadItemTypeOptions = async (_companyId?: string, force = false): Promise<InventoryItemOption[]> => {
  const key = '__all__';
  if (!force && loadedTypeCompanies.has(key)) {
    return getItemTypeOptions();
  }

  const apiOptions = await getMaterialTypeOptions();
  mutableItemTypeOptions.length = 0;
  mutableItemTypeOptions.push(...apiOptions);

  loadedTypeCompanies.add(key);
  return getItemTypeOptions();
};

export const createItemGroupOption = async (name: string, companyId: string): Promise<InventoryItemOption> => {
  if (!companyId) {
    throw new Error('Vui lòng chọn công ty trước khi tạo nhóm vật tư');
  }

  const created = await createMaterialGroupOption({
    name: name.trim(),
    company_id: companyId,
  });

  const normalized = {
    ...created,
    company_id: created.company_id || companyId,
  };

  upsertGroupOption(normalized);
  loadedGroupCompanies.add('__all__');
  return normalized;
};

export const createItemSupplierOption = async (
  name: string,
  companyId: string,
  details?: Omit<InventoryItemSupplierDraft, 'name'>,
): Promise<InventoryItemOption> => {
  if (!companyId) {
    throw new Error('Vui lòng chọn công ty trước khi tạo nhà cung cấp');
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Tên nhà cung cấp là bắt buộc');
  }

  const createdSupplier = await createSupplierAPI({
    name: trimmedName,
    email: details?.email,
    phone: details?.phone,
    status: 'active',
    priority: 'medium',
    // Use same payload contract as NewSupplier: backend creates Address from address_obj.
    address_obj: details?.address
      ? {
        address_line: details.address.address_line,
        free_address: details.address.free_address,
        ward: details.address.ward,
        province: details.address.province,
      }
      : undefined,
    // Match supplier module company mapping behavior.
    ...((companyId && companyId.trim()) ? { company_ids: [companyId] } : {}),
  } as any);

  const supplierId = await resolveCreatedSupplierId(companyId, createdSupplier, {
    name: trimmedName,
    email: details?.email,
    phone: details?.phone,
    address: details?.address,
  });

  if (supplierId && !createdSupplier?.companies?.some((company: any) => company.id === companyId)) {
    try {
      await addSupplierToCompany(supplierId, companyId);
    } catch (_error) {
      // Ignore mapping retry error here; supplier is already created.
    }
  }

  if (!supplierId) {
    throw new Error('Không thể tạo nhà cung cấp trên hệ thống. Vui lòng thử lại.');
  }

  const nextOption = {
    id: supplierId,
    name: createdSupplier?.name || trimmedName,
    company_id: companyId,
  };

  upsertSupplierOption(nextOption);
  loadedSupplierCompanies.add(companyId);

  mutableSupplierDrafts.push({
    id: nextOption.id,
    company_id: companyId,
    name: nextOption.name,
    email: details?.email,
    phone: details?.phone,
    address: details?.address,
  });

  return nextOption;
};

export const createItemManufacturerOption = async (name: string, companyId: string): Promise<InventoryItemOption> => {
  if (!companyId) {
    throw new Error('Vui lòng chọn công ty trước khi tạo nhà sản xuất');
  }

  const created = await createManufacturerOption({
    name: name.trim(),
    company_id: companyId,
  });

  const normalized = {
    ...created,
    company_id: created.company_id || companyId,
  };

  upsertManufacturerOption(normalized);
  loadedManufacturerCompanies.add(companyId);
  return normalized;
};

export const createItemTypeOption = async (name: string, companyId: string): Promise<InventoryItemOption> => {
  if (!companyId) {
    throw new Error('Vui lòng chọn công ty trước khi tạo loại vật tư');
  }

  const created = await createMaterialTypeOption({
    name: name.trim(),
    company_id: companyId,
  });

  const normalized = {
    ...created,
    company_id: created.company_id || companyId,
  };

  upsertTypeOption(normalized);
  loadedTypeCompanies.add('__all__');
  return normalized;
};

export const createItemUomOption = async (
  name: string,
  companyId: string,
  conversion?: { to_uom_id: string; ratio: number },
): Promise<InventoryItemOption> => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Tên ĐVT là bắt buộc');
  }

  if (!companyId) {
    throw new Error('Vui lòng chọn công ty trước khi tạo ĐVT');
  }

  // Create UOM in backend first
  const backendUom = await createItemUomBackend(trimmedName, conversion);

  // Create UOM option with company scope
  const nextOption: InventoryItemOption = {
    id: backendUom.id,
    name: backendUom.name,
    company_id: companyId,
  };

  // Add to local mutable array
  mutableUomOptions.push(nextOption);
  loadedUomCompanies.add(companyId);

  // Store conversion if provided
  if (conversion && conversion.to_uom_id && conversion.ratio > 0) {
    mutableUomConversions.push({
      id: `conv-${Date.now()}`,
      from_uom_id: nextOption.id,
      to_uom_id: conversion.to_uom_id,
      ratio: conversion.ratio,
      company_id: companyId,
    });
  }

  return nextOption;
};

export const getItemUomConversions = (companyId?: string): InventoryItemUomConversion[] => {
  if (!companyId) {
    return [...mutableUomConversions];
  }

  return mutableUomConversions.filter((entry) => entry.company_id === companyId);
};

const getOptionNameById = (options: InventoryItemOption[], optionId: string) => options.find((option) => option.id === optionId)?.name || '';

export const createDefaultItemForm = (companyId: string): InventoryItemFormData => {
  const uomOptions = getItemUomOptions(companyId);
  const manufacturerOptions = getItemManufacturerOptions(companyId);
  const supplierOptions = getItemSupplierOptions(companyId);
  const groupOptions = getItemGroupOptions(companyId);

  const defaultUomId = uomOptions[0]?.id || '';

  return {
    code: '',
    name: '',
    group_id: groupOptions[0]?.id || '',
    supplier_id: supplierOptions[0]?.id || '',
    item_type: 'material',
    uom_id: defaultUomId,
    manufacturer_id: manufacturerOptions[0]?.id || '',
    status: 'yeu_cau_vat_tu',
    company_id: companyId,
    specification: '',
    image1: '',
    image2: '',
    track_lot: false,
    track_location: false,
  };
};

export const buildItemRowFromForm = (formData: InventoryItemFormData, itemId: string): InventoryItemRow => {
  const groupOptions = getItemGroupOptions(formData.company_id);
  const supplierOptions = getItemSupplierOptions(formData.company_id);
  const manufacturerOptions = getItemManufacturerOptions(formData.company_id);
  const uomOptions = getItemUomOptions(formData.company_id);

  return {
    id: itemId,
    code: formData.code.trim(),
    name: formData.name.trim(),
    item_type: formData.item_type,
    item_type_label: resolveItemTypeLabel(formData.item_type),
    supplier_id: formData.supplier_id,
    supplier_name: getOptionNameById(supplierOptions, formData.supplier_id),
    manufacturer_id: formData.manufacturer_id,
    manufacturer_name: getOptionNameById(manufacturerOptions, formData.manufacturer_id),
    group_id: formData.group_id,
    group_name: getOptionNameById(groupOptions, formData.group_id),
    uom_id: formData.uom_id,
    uom_name: getOptionNameById(uomOptions, formData.uom_id),
    status: formData.status,
    status_label: resolveStatusLabel(formData.status),
    company_id: formData.company_id,
    company_name: getCompanyName(formData.company_id),
    specification: formData.specification.trim(),
    image1: formData.image1,
    image2: formData.image2,
    track_lot: formData.track_lot,
    track_location: formData.track_location,
  };
};

export const createInitialItemRows = (): InventoryItemRow[] => [];

export const upsertItemDetailOptions = (item: any) => {
  const companyId = item.company?.id || item.company_id || '';
  if (!companyId) return;

  if (item.group?.id && item.group?.name) {
    upsertGroupOption({ id: String(item.group.id), name: item.group.name, company_id: companyId });
  }
  if (item.supplier?.id && item.supplier?.name) {
    upsertSupplierOption({ id: String(item.supplier.id), name: item.supplier.name, company_id: companyId });
  }
  if (item.manufacturer?.id && item.manufacturer?.name) {
    upsertManufacturerOption({ id: String(item.manufacturer.id), name: item.manufacturer.name, company_id: companyId });
  }
  if (item.uom?.id && item.uom?.name) {
    const exists = mutableUomOptions.some((u) => u.id === String(item.uom.id) && u.company_id === companyId);
    if (!exists) {
      mutableUomOptions.push({ id: String(item.uom.id), name: item.uom.name, company_id: companyId });
    }
  }
  if (typeof item.item_type === 'object' && item.item_type?.id && item.item_type?.name) {
    const exists = mutableItemTypeOptions.some((t) => t.id === String(item.item_type.id) && t.company_id === companyId);
    if (!exists) {
      mutableItemTypeOptions.push({ id: String(item.item_type.id), name: item.item_type.name, company_id: companyId });
    }
  }
};
